/**
 * Congress Trading Page
 *
 * Tracks stock trades by US Congress members (STOCK Act disclosures).
 * Design inspired by WalletTracker and HederaMonitor pages.
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Icons } from '../components/Icons';
import { useAuthStore } from '../store/authStore';
import {
  fetchCongressTrades,
  fetchPoliticians,
  formatAmountRange,
  formatRelativeDate,
  type CongressTrade,
  type CongressPolitician,
  type TradeFilters,
} from '../services/congressTradingApi';

// ============================================
// CONSTANTS & STYLES
// ============================================

const CONGRESS_COLORS = {
  primary: '#818cf8', // Indigo
  secondary: '#6366f1', // Deeper indigo
  accent: '#a78bfa', // Violet
  gradient: 'linear-gradient(135deg, #818cf8, #6366f1)',
  lightGradient: 'linear-gradient(135deg, rgba(129, 140, 248, 0.15), rgba(99, 102, 241, 0.1))',
  democrat: '#3b82f6', // Blue
  republican: '#ef4444', // Red
  independent: '#a855f7', // Purple
  buy: '#10b981', // Green
  sell: '#ef4444', // Red
  exchange: '#f59e0b', // Amber
};

const s = {
  page: {
    padding: '0',
    maxWidth: '1400px',
    margin: '0 auto',
    paddingBottom: '40px',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '24px',
    gap: '20px',
    flexWrap: 'wrap' as const,
  },
  title: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  titleIcon: {
    width: '40px',
    height: '40px',
    borderRadius: '12px',
    background: CONGRESS_COLORS.gradient,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  titleText: {
    fontSize: '24px',
    fontWeight: 700,
    margin: 0,
  },
  subtitle: {
    fontSize: '13px',
    color: 'var(--muted)',
    marginTop: '4px',
  },
  card: {
    background: 'rgba(0, 0, 0, 0.25)',
    backdropFilter: 'blur(20px)',
    borderRadius: '16px',
    border: '1px solid rgba(255, 255, 255, 0.08)',
    padding: '20px',
    marginBottom: '20px',
  },
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
    gap: '16px',
    marginBottom: '24px',
  },
  statCard: (color: string) => ({
    background: `${color}10`,
    border: `1px solid ${color}30`,
    borderRadius: '12px',
    padding: '16px',
    textAlign: 'center' as const,
  }),
  statValue: {
    fontSize: '22px',
    fontWeight: 700,
  },
  statLabel: {
    fontSize: '11px',
    color: 'var(--muted)',
    marginTop: '4px',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.5px',
  },
  tabContainer: {
    display: 'flex',
    gap: '4px',
    padding: '4px',
    background: 'rgba(0, 0, 0, 0.3)',
    borderRadius: '14px',
    width: 'fit-content',
    marginBottom: '24px',
  },
  tab: (isActive: boolean) => ({
    padding: '10px 18px',
    borderRadius: '10px',
    border: 'none',
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: 600,
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    transition: 'all 0.3s ease',
    background: isActive ? CONGRESS_COLORS.gradient : 'transparent',
    color: isActive ? 'white' : 'var(--muted)',
    transform: isActive ? 'scale(1.02)' : 'scale(1)',
    boxShadow: isActive ? '0 4px 15px rgba(129, 140, 248, 0.3)' : 'none',
  }),
  filterBar: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    flexWrap: 'wrap' as const,
    marginBottom: '20px',
  },
  filterSelect: {
    padding: '8px 12px',
    background: 'rgba(255, 255, 255, 0.06)',
    border: '1px solid rgba(255, 255, 255, 0.12)',
    borderRadius: '10px',
    fontSize: '13px',
    color: 'var(--text)',
    cursor: 'pointer',
    outline: 'none',
  },
  searchInput: {
    flex: 1,
    minWidth: '200px',
    padding: '10px 14px 10px 38px',
    background: 'rgba(255, 255, 255, 0.06)',
    border: '1px solid rgba(255, 255, 255, 0.12)',
    borderRadius: '10px',
    fontSize: '13px',
    color: 'var(--text)',
    outline: 'none',
  },
  politicianCard: (isSelected: boolean, partyColor: string) => ({
    background: isSelected
      ? `${partyColor}15`
      : 'rgba(255, 255, 255, 0.04)',
    border: `1px solid ${isSelected ? `${partyColor}40` : 'rgba(255, 255, 255, 0.08)'}`,
    borderRadius: '14px',
    padding: '16px',
    cursor: 'pointer',
    transition: 'all 0.25s ease',
    textAlign: 'left' as const,
    position: 'relative' as const,
    overflow: 'hidden' as const,
  }),
  avatar: (color: string) => ({
    width: '36px',
    height: '36px',
    borderRadius: '10px',
    background: `${color}20`,
    border: `1px solid ${color}40`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '14px',
    fontWeight: 700,
    color: color,
    flexShrink: 0,
  }),
  tradeRow: {
    display: 'grid',
    gridTemplateColumns: '100px 1.5fr 100px 100px 120px 80px',
    alignItems: 'center',
    padding: '14px 20px',
    borderBottom: '1px solid rgba(255, 255, 255, 0.04)',
    transition: 'background 0.15s ease',
    cursor: 'default',
    gap: '8px',
  },
  tradeRowMobile: {
    padding: '14px 16px',
    borderBottom: '1px solid rgba(255, 255, 255, 0.04)',
  },
  badge: (bg: string, text: string) => ({
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    padding: '3px 8px',
    borderRadius: '6px',
    fontSize: '11px',
    fontWeight: 600,
    background: bg,
    color: text,
  }),
  tickerBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '4px 10px',
    borderRadius: '8px',
    fontSize: '12px',
    fontWeight: 700,
    fontFamily: 'monospace',
    background: 'rgba(255, 255, 255, 0.08)',
    color: 'white',
    border: '1px solid rgba(255, 255, 255, 0.12)',
    letterSpacing: '0.5px',
  },
  pagination: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '14px 20px',
    borderTop: '1px solid rgba(255, 255, 255, 0.06)',
  },
  paginationButton: (disabled: boolean) => ({
    padding: '8px 14px',
    borderRadius: '8px',
    border: 'none',
    background: disabled ? 'transparent' : 'rgba(255, 255, 255, 0.08)',
    color: disabled ? 'rgba(255, 255, 255, 0.2)' : 'var(--text)',
    fontSize: '12px',
    fontWeight: 600,
    cursor: disabled ? 'not-allowed' : 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    transition: 'all 0.2s',
  }),
};

// ============================================
// HELPERS
// ============================================

function getPartyColor(party: string | null): string {
  switch (party) {
    case 'Democrat': return CONGRESS_COLORS.democrat;
    case 'Republican': return CONGRESS_COLORS.republican;
    case 'Independent': return CONGRESS_COLORS.independent;
    default: return '#9ca3af';
  }
}

function getInitials(name: string): string {
  return name.split(' ').map(n => n[0]).slice(0, 2).join('').toUpperCase();
}

function getTradeColor(type: string): { bg: string; text: string; label: string } {
  switch (type) {
    case 'purchase':
      return { bg: 'rgba(16, 185, 129, 0.15)', text: '#10b981', label: 'BUY' };
    case 'sale_full':
      return { bg: 'rgba(239, 68, 68, 0.15)', text: '#ef4444', label: 'SELL' };
    case 'sale_partial':
      return { bg: 'rgba(249, 115, 22, 0.15)', text: '#f97316', label: 'SELL' };
    case 'exchange':
      return { bg: 'rgba(245, 158, 11, 0.15)', text: '#f59e0b', label: 'SWAP' };
    default:
      return { bg: 'rgba(156, 163, 175, 0.15)', text: '#9ca3af', label: type };
  }
}

// ============================================
// SUB-COMPONENTS
// ============================================

function StatsBanner({ politicians, trades, lastSync }: {
  politicians: CongressPolitician[];
  trades: CongressTrade[];
  lastSync: string | null;
}) {
  const { t } = useTranslation();

  const totalTrades = useMemo(() =>
    politicians.reduce((sum, p) => sum + p.total_trades, 0), [politicians]);

  const recentBuys = trades.filter(t => t.trade_type === 'purchase').length;
  const recentSells = trades.filter(t => t.trade_type !== 'purchase').length;

  return (
    <div style={{
      ...s.card,
      background: CONGRESS_COLORS.lightGradient,
      border: '1px solid rgba(129, 140, 248, 0.25)',
      padding: '24px',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '16px' }}>
        <div>
          <div style={{ fontSize: '12px', color: 'var(--muted)', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            {t('congressTrading.subtitle', 'STOCK Act Public Disclosures')}
          </div>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px' }}>
            <span style={{ fontSize: '32px', fontWeight: 700, color: CONGRESS_COLORS.primary }}>
              {politicians.length}
            </span>
            <span style={{ fontSize: '14px', color: 'var(--muted)' }}>
              {t('congressTrading.featuredPoliticians', 'politicians tracked')}
            </span>
          </div>
        </div>

        <div style={{ display: 'flex', gap: '28px', flexWrap: 'wrap' }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '20px', fontWeight: 700, color: CONGRESS_COLORS.primary }}>
              {totalTrades.toLocaleString()}
            </div>
            <div style={{ fontSize: '11px', color: 'var(--muted)', marginTop: '2px' }}>
              {t('congressTrading.totalTrades', 'Total Trades')}
            </div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '20px', fontWeight: 700, color: CONGRESS_COLORS.buy }}>
              {recentBuys}
            </div>
            <div style={{ fontSize: '11px', color: 'var(--muted)', marginTop: '2px' }}>
              {t('congressTrading.purchases', 'Purchases')}
            </div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '20px', fontWeight: 700, color: CONGRESS_COLORS.sell }}>
              {recentSells}
            </div>
            <div style={{ fontSize: '11px', color: 'var(--muted)', marginTop: '2px' }}>
              {t('congressTrading.sales', 'Sales')}
            </div>
          </div>
          {lastSync && (
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text)' }}>
                {formatRelativeDate(lastSync)}
              </div>
              <div style={{ fontSize: '11px', color: 'var(--muted)', marginTop: '2px' }}>
                {t('congressTrading.lastSync', 'Last Sync')}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function PoliticianCard({ politician, isSelected, onClick }: {
  politician: CongressPolitician;
  isSelected: boolean;
  onClick: () => void;
}) {
  const partyColor = getPartyColor(politician.party);

  return (
    <div
      onClick={onClick}
      style={s.politicianCard(isSelected, partyColor)}
      onMouseEnter={(e) => {
        if (!isSelected) {
          (e.currentTarget as HTMLDivElement).style.background = 'rgba(255, 255, 255, 0.07)';
          (e.currentTarget as HTMLDivElement).style.transform = 'translateY(-2px)';
        }
      }}
      onMouseLeave={(e) => {
        if (!isSelected) {
          (e.currentTarget as HTMLDivElement).style.background = 'rgba(255, 255, 255, 0.04)';
          (e.currentTarget as HTMLDivElement).style.transform = 'translateY(0)';
        }
      }}
    >
      {isSelected && (
        <div style={{
          position: 'absolute',
          top: '8px',
          right: '8px',
          width: '8px',
          height: '8px',
          borderRadius: '50%',
          background: partyColor,
          boxShadow: `0 0 8px ${partyColor}`,
        }} />
      )}
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
        <div style={s.avatar(partyColor)}>
          {getInitials(politician.name)}
        </div>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {politician.name}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '4px' }}>
            <span style={{
              ...s.badge(`${partyColor}20`, partyColor),
              fontSize: '10px',
              padding: '2px 6px',
            }}>
              {politician.party === 'Democrat' ? 'DEM' : politician.party === 'Republican' ? 'REP' : 'IND'}
            </span>
            <span style={{ fontSize: '11px', color: 'var(--muted)' }}>
              {politician.chamber === 'house' ? 'H' : 'S'} - {politician.state}
            </span>
          </div>
        </div>
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '12px', paddingTop: '10px', borderTop: '1px solid rgba(255,255,255,0.06)' }}>
        <span style={{ fontSize: '11px', color: 'var(--muted)' }}>
          {politician.total_trades} trades
        </span>
        {politician.last_trade_date && (
          <span style={{ fontSize: '10px', color: 'var(--muted)' }}>
            {formatRelativeDate(politician.last_trade_date)}
          </span>
        )}
      </div>
    </div>
  );
}

function TradeRowDesktop({ trade, onPoliticianClick }: {
  trade: CongressTrade;
  onPoliticianClick: (name: string) => void;
}) {
  const tradeColor = getTradeColor(trade.trade_type);
  const partyColor = getPartyColor(trade.party);

  return (
    <div
      style={s.tradeRow}
      onMouseEnter={(e) => {
        (e.currentTarget as HTMLDivElement).style.background = 'rgba(255, 255, 255, 0.03)';
      }}
      onMouseLeave={(e) => {
        (e.currentTarget as HTMLDivElement).style.background = 'transparent';
      }}
    >
      {/* Date */}
      <div>
        <div style={{ fontSize: '12px', color: 'var(--text)', fontWeight: 500 }}>
          {new Date(trade.transaction_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
        </div>
        <div style={{ fontSize: '10px', color: 'var(--muted)', marginTop: '2px' }}>
          {new Date(trade.transaction_date).getFullYear()}
        </div>
      </div>

      {/* Politician */}
      <div
        style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', minWidth: 0 }}
        onClick={() => onPoliticianClick(trade.politician_name)}
      >
        <div style={{ ...s.avatar(partyColor), width: '30px', height: '30px', fontSize: '11px', borderRadius: '8px' }}>
          {getInitials(trade.politician_name)}
        </div>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {trade.politician_name}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginTop: '2px' }}>
            <span style={{ fontSize: '10px', color: partyColor, fontWeight: 600 }}>
              {trade.party === 'Democrat' ? 'D' : trade.party === 'Republican' ? 'R' : 'I'}
            </span>
            <span style={{ fontSize: '10px', color: 'var(--muted)' }}>
              {trade.chamber === 'house' ? 'House' : 'Senate'}{trade.state ? ` - ${trade.state}` : ''}
            </span>
          </div>
        </div>
      </div>

      {/* Ticker */}
      <div>
        {trade.ticker ? (
          <span style={s.tickerBadge}>{trade.ticker}</span>
        ) : (
          <span style={{ fontSize: '11px', color: 'var(--muted)', fontStyle: 'italic' }}>
            {(trade.asset_description || 'N/A').slice(0, 15)}
          </span>
        )}
      </div>

      {/* Type */}
      <div>
        <span style={s.badge(tradeColor.bg, tradeColor.text)}>
          {trade.trade_type === 'purchase' ? (
            <Icons.TrendingUp size={11} />
          ) : (
            <Icons.TrendingDown size={11} />
          )}
          {tradeColor.label}
        </span>
      </div>

      {/* Amount */}
      <div style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text)' }}>
        {formatAmountRange(trade.amount_low, trade.amount_high)}
      </div>

      {/* Owner */}
      <div style={{ fontSize: '11px', color: 'var(--muted)' }}>
        {trade.owner || 'Self'}
      </div>
    </div>
  );
}

function TradeRowMobile({ trade, onPoliticianClick }: {
  trade: CongressTrade;
  onPoliticianClick: (name: string) => void;
}) {
  const tradeColor = getTradeColor(trade.trade_type);
  const partyColor = getPartyColor(trade.party);

  return (
    <div style={s.tradeRowMobile}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', flex: 1, minWidth: 0 }}
          onClick={() => onPoliticianClick(trade.politician_name)}>
          <div style={{ ...s.avatar(partyColor), width: '28px', height: '28px', fontSize: '10px', borderRadius: '8px' }}>
            {getInitials(trade.politician_name)}
          </div>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {trade.politician_name}
            </div>
            <div style={{ fontSize: '11px', color: 'var(--muted)', marginTop: '1px' }}>
              {new Date(trade.transaction_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
            </div>
          </div>
        </div>
        <span style={s.badge(tradeColor.bg, tradeColor.text)}>
          {trade.trade_type === 'purchase' ? <Icons.TrendingUp size={10} /> : <Icons.TrendingDown size={10} />}
          {tradeColor.label}
        </span>
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '10px', paddingLeft: '38px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {trade.ticker && <span style={{ ...s.tickerBadge, fontSize: '11px', padding: '2px 8px' }}>{trade.ticker}</span>}
          <span style={{ fontSize: '11px', color: partyColor, fontWeight: 600 }}>
            {trade.party === 'Democrat' ? 'D' : 'R'}
          </span>
        </div>
        <span style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text)' }}>
          {formatAmountRange(trade.amount_low, trade.amount_high)}
        </span>
      </div>
    </div>
  );
}

// ============================================
// MAIN COMPONENT
// ============================================

export function CongressTrading() {
  const { isAuthenticated } = useAuthStore();
  // Branch on auth at the wrapper level so the inner component (which owns
  // all the hooks) is only mounted when the user is authenticated. Putting
  // the early return BEFORE hooks in a single component violates the rules
  // of hooks: the hook order changes when the user logs in/out, breaking
  // React's reconciler. The split keeps the hook order stable inside
  // `CongressTradingAuthenticated`.
  if (!isAuthenticated) {
    return <CongressTradingLoginGate />;
  }
  return <CongressTradingAuthenticated />;
}

function CongressTradingLoginGate() {
  const { t } = useTranslation();
  return (
    <div style={s.page}>
      <div style={s.header}>
        <div>
          <div style={s.title}>
            <div style={s.titleIcon}>
              <Icons.Briefcase size={20} color="white" />
            </div>
            <h1 style={s.titleText}>{t('congressTrading.title', 'Congress Trading Tracker')}</h1>
          </div>
          <p style={s.subtitle}>
            {t('congressTrading.subtitle', 'Track stock trades by US Congress members (STOCK Act public disclosures)')}
          </p>
        </div>
      </div>

      <div style={{
        ...s.card,
        display: 'flex',
        flexDirection: 'column' as const,
        alignItems: 'center',
        justifyContent: 'center',
        padding: '60px 20px',
        textAlign: 'center' as const,
      }}>
        <div style={{
          width: '64px',
          height: '64px',
          borderRadius: '16px',
          background: 'rgba(129, 140, 248, 0.15)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          marginBottom: '20px',
        }}>
          <Icons.Lock size={32} color={CONGRESS_COLORS.primary} />
        </div>
        <h2 style={{ fontSize: '20px', fontWeight: 600, marginBottom: '8px' }}>
          {t('congressTrading.loginRequired', 'Connexion requise')}
        </h2>
        <p style={{ color: 'var(--muted)', fontSize: '14px', maxWidth: '360px' }}>
          {t('congressTrading.loginMessage', 'Connectez-vous pour accéder au Congress Trading Tracker et suivre les transactions des membres du Congrès.')}
        </p>
      </div>
    </div>
  );
}

function CongressTradingAuthenticated() {
  const { t } = useTranslation();

  // State
  const [trades, setTrades] = useState<CongressTrade[]>([]);
  const [politicians, setPoliticians] = useState<CongressPolitician[]>([]);
  const [selectedPolitician, setSelectedPolitician] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isPoliticiansLoading, setIsPoliticiansLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastSync, setLastSync] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'trades' | 'politicians'>('trades');

  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [chamberFilter, setChamberFilter] = useState<'all' | 'house' | 'senate'>('all');
  const [tradeTypeFilter, setTradeTypeFilter] = useState<'all' | 'purchase' | 'sale'>('all');
  const [daysFilter, setDaysFilter] = useState(90);
  const [currentPage, setCurrentPage] = useState(0);
  const PAGE_SIZE = 30;

  // Fetch politicians
  useEffect(() => {
    async function loadPoliticians() {
      setIsPoliticiansLoading(true);
      try {
        const result = await fetchPoliticians(true);
        setPoliticians(result.data || []);
      } catch (err) {
        // Silently handle - politicians are non-critical
      } finally {
        setIsPoliticiansLoading(false);
      }
    }
    loadPoliticians();
  }, []);

  // Fetch trades
  const loadTrades = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const filters: TradeFilters = {
        days: daysFilter,
        limit: PAGE_SIZE,
        offset: currentPage * PAGE_SIZE,
        chamber: chamberFilter,
        trade_type: tradeTypeFilter,
      };

      if (selectedPolitician) {
        filters.politician = selectedPolitician;
      } else if (searchQuery.trim()) {
        if (/^[A-Z]{1,5}$/.test(searchQuery.trim())) {
          filters.ticker = searchQuery.trim();
        } else {
          filters.politician = searchQuery.trim();
        }
      }

      const result = await fetchCongressTrades(filters);
      setTrades(result.data || []);
      setLastSync(result.lastSync);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch trades');
      setTrades([]);
    } finally {
      setIsLoading(false);
    }
  }, [daysFilter, currentPage, chamberFilter, tradeTypeFilter, selectedPolitician, searchQuery]);

  useEffect(() => {
    loadTrades();
  }, [loadTrades]);

  useEffect(() => {
    setCurrentPage(0);
  }, [chamberFilter, tradeTypeFilter, daysFilter, selectedPolitician, searchQuery]);

  const handlePoliticianClick = (name: string) => {
    if (selectedPolitician === name) {
      setSelectedPolitician(null);
    } else {
      setSelectedPolitician(name);
      setSearchQuery('');
      setActiveTab('trades');
    }
  };

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSelectedPolitician(null);
    setCurrentPage(0);
    loadTrades();
  };

  const clearFilters = () => {
    setSelectedPolitician(null);
    setSearchQuery('');
    setChamberFilter('all');
    setTradeTypeFilter('all');
    setDaysFilter(90);
  };

  const hasActiveFilters = selectedPolitician || searchQuery || chamberFilter !== 'all' || tradeTypeFilter !== 'all';

  return (
    <div style={s.page}>
      {/* Header */}
      <div style={s.header}>
        <div style={s.title}>
          <div style={s.titleIcon}>
            <Icons.Briefcase size={20} className="" style={{ color: 'white' }} />
          </div>
          <div>
            <h1 style={s.titleText}>{t('congressTrading.title', 'Congress Trading Tracker')}</h1>
            <p style={s.subtitle}>
              {t('congressTrading.subtitle', 'Track stock trades by US Congress members (STOCK Act public disclosures)')}
            </p>
          </div>
        </div>
      </div>

      {/* Stats Banner */}
      <StatsBanner politicians={politicians} trades={trades} lastSync={lastSync} />

      {/* Tabs */}
      <div style={s.tabContainer}>
        <button
          style={s.tab(activeTab === 'trades')}
          onClick={() => setActiveTab('trades')}
        >
          <Icons.Activity size={15} />
          {t('congressTrading.tradesFeed', 'Trades Feed')}
        </button>
        <button
          style={s.tab(activeTab === 'politicians')}
          onClick={() => setActiveTab('politicians')}
        >
          <Icons.Users size={15} />
          {t('congressTrading.featuredPoliticians', 'Politicians')}
        </button>
      </div>

      {/* Tab Content */}
      {activeTab === 'politicians' ? (
        /* Politicians Grid */
        <div style={s.card}>
          {isPoliticiansLoading ? (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '40px' }}>
              <Icons.Loader2 size={24} className="animate-spin" style={{ color: CONGRESS_COLORS.primary }} />
            </div>
          ) : (
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
              gap: '12px',
            }}>
              {politicians.map((pol) => (
                <PoliticianCard
                  key={pol.id}
                  politician={pol}
                  isSelected={selectedPolitician === pol.name}
                  onClick={() => handlePoliticianClick(pol.name)}
                />
              ))}
            </div>
          )}
        </div>
      ) : (
        /* Trades Feed */
        <>
          {/* Filter Bar */}
          <div style={s.filterBar}>
            <form onSubmit={handleSearchSubmit} style={{ flex: 1, minWidth: '200px', position: 'relative' }}>
              <Icons.Search size={15} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'var(--muted)' }} />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={t('congressTrading.searchPlaceholder', 'Search politician or ticker (e.g. AAPL)...')}
                style={s.searchInput}
              />
            </form>

            <select aria-label={t('congressTrading.chamberFilter', 'Filter by chamber')} value={chamberFilter} onChange={(e) => setChamberFilter(e.target.value as any)} style={s.filterSelect}>
              <option value="all">{t('congressTrading.allChambers', 'All Chambers')}</option>
              <option value="house">{t('congressTrading.house', 'House')}</option>
              <option value="senate">{t('congressTrading.senate', 'Senate')}</option>
            </select>

            <select aria-label={t('congressTrading.tradeTypeFilter', 'Filter by trade type')} value={tradeTypeFilter} onChange={(e) => setTradeTypeFilter(e.target.value as any)} style={s.filterSelect}>
              <option value="all">{t('congressTrading.allTypes', 'All Types')}</option>
              <option value="purchase">{t('congressTrading.purchases', 'Purchases')}</option>
              <option value="sale">{t('congressTrading.sales', 'Sales')}</option>
            </select>

            <select aria-label={t('congressTrading.daysFilter', 'Filter by time period')} value={daysFilter} onChange={(e) => setDaysFilter(Number(e.target.value))} style={s.filterSelect}>
              <option value={30}>30 {t('congressTrading.days', 'days')}</option>
              <option value={90}>90 {t('congressTrading.days', 'days')}</option>
              <option value={180}>180 {t('congressTrading.days', 'days')}</option>
              <option value={365}>1 {t('congressTrading.year', 'year')}</option>
              <option value={730}>2 {t('congressTrading.years', 'years')}</option>
            </select>

            {hasActiveFilters && (
              <button
                onClick={clearFilters}
                style={{ padding: '8px 12px', borderRadius: '8px', border: 'none', background: 'rgba(239, 68, 68, 0.1)', color: '#ef4444', fontSize: '12px', fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }}
              >
                <Icons.X size={12} />
                {t('congressTrading.clearFilters', 'Clear')}
              </button>
            )}
          </div>

          {/* Selected politician indicator */}
          {selectedPolitician && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
              <span style={{ fontSize: '12px', color: 'var(--muted)' }}>{t('congressTrading.filteringBy', 'Filtering by')}:</span>
              <span style={{
                ...s.badge('rgba(129, 140, 248, 0.15)', CONGRESS_COLORS.primary),
                padding: '4px 10px',
                fontSize: '12px',
                gap: '6px',
              }}>
                <Icons.User size={12} />
                {selectedPolitician}
                <span
                  onClick={() => setSelectedPolitician(null)}
                  style={{ cursor: 'pointer', marginLeft: '4px', opacity: 0.7 }}
                >
                  <Icons.X size={11} />
                </span>
              </span>
            </div>
          )}

          {/* Trades Table */}
          <div style={{ ...s.card, padding: 0, overflow: 'hidden' }}>
            {isLoading ? (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '60px 20px' }}>
                <Icons.Loader2 size={28} className="animate-spin" style={{ color: CONGRESS_COLORS.primary }} />
                <span style={{ marginTop: '12px', fontSize: '13px', color: 'var(--muted)' }}>
                  {t('congressTrading.loading', 'Loading trades...')}
                </span>
              </div>
            ) : error ? (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '60px 20px' }}>
                <Icons.AlertCircle size={28} style={{ color: '#ef4444' }} />
                <span style={{ marginTop: '12px', fontSize: '13px', color: '#ef4444' }}>{error}</span>
                <button
                  onClick={loadTrades}
                  style={{ marginTop: '12px', padding: '8px 16px', background: CONGRESS_COLORS.gradient, border: 'none', borderRadius: '8px', color: 'white', fontSize: '12px', fontWeight: 600, cursor: 'pointer' }}
                >
                  {t('congressTrading.retry', 'Retry')}
                </button>
              </div>
            ) : trades.length === 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '60px 20px' }}>
                <Icons.Briefcase size={32} style={{ color: 'var(--muted)', opacity: 0.5 }} />
                <span style={{ marginTop: '12px', fontSize: '14px', color: 'var(--muted)' }}>
                  {t('congressTrading.noTrades', 'No trades found for the selected criteria')}
                </span>
                {hasActiveFilters && (
                  <button onClick={clearFilters} style={{ marginTop: '12px', fontSize: '12px', color: CONGRESS_COLORS.primary, background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}>
                    {t('congressTrading.clearFilters', 'Clear filters')}
                  </button>
                )}
              </div>
            ) : (
              <>
                {/* Table header - desktop only */}
                <div className="hidden md:grid" style={{
                  gridTemplateColumns: '100px 1.5fr 100px 100px 120px 80px',
                  padding: '12px 20px',
                  borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
                  gap: '8px',
                }}>
                  <span style={{ fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--muted)', fontWeight: 600 }}>{t('congressTrading.date', 'Date')}</span>
                  <span style={{ fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--muted)', fontWeight: 600 }}>{t('congressTrading.politician', 'Politician')}</span>
                  <span style={{ fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--muted)', fontWeight: 600 }}>{t('congressTrading.ticker', 'Ticker')}</span>
                  <span style={{ fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--muted)', fontWeight: 600 }}>{t('congressTrading.type', 'Type')}</span>
                  <span style={{ fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--muted)', fontWeight: 600 }}>{t('congressTrading.amount', 'Amount')}</span>
                  <span style={{ fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--muted)', fontWeight: 600 }}>{t('congressTrading.owner', 'Owner')}</span>
                </div>

                {/* Desktop rows */}
                <div className="hidden md:block">
                  {trades.map((trade) => (
                    <TradeRowDesktop key={trade.id} trade={trade} onPoliticianClick={handlePoliticianClick} />
                  ))}
                </div>

                {/* Mobile rows */}
                <div className="md:hidden">
                  {trades.map((trade) => (
                    <TradeRowMobile key={trade.id} trade={trade} onPoliticianClick={handlePoliticianClick} />
                  ))}
                </div>

                {/* Pagination */}
                <div style={s.pagination}>
                  <button
                    onClick={() => setCurrentPage(p => Math.max(0, p - 1))}
                    disabled={currentPage === 0}
                    style={s.paginationButton(currentPage === 0)}
                  >
                    <Icons.ChevronLeft size={14} />
                    {t('congressTrading.previous', 'Previous')}
                  </button>
                  <span style={{ fontSize: '12px', color: 'var(--muted)' }}>
                    {t('congressTrading.page', 'Page')} {currentPage + 1}
                  </span>
                  <button
                    onClick={() => setCurrentPage(p => p + 1)}
                    disabled={trades.length < PAGE_SIZE}
                    style={s.paginationButton(trades.length < PAGE_SIZE)}
                  >
                    {t('congressTrading.next', 'Next')}
                    <Icons.ChevronRight size={14} />
                  </button>
                </div>
              </>
            )}
          </div>
        </>
      )}

      {/* Disclaimer */}
      <div style={{
        ...s.card,
        background: 'rgba(0, 0, 0, 0.15)',
        border: '1px solid rgba(255, 255, 255, 0.04)',
        padding: '14px 18px',
        display: 'flex',
        alignItems: 'flex-start',
        gap: '10px',
      }}>
        <Icons.Info size={14} style={{ color: 'var(--muted)', flexShrink: 0, marginTop: '2px' }} />
        <p style={{ fontSize: '11px', color: 'var(--muted)', lineHeight: '1.5', margin: 0 }}>
          {t('congressTrading.disclaimer', 'Data sourced from STOCK Act public disclosures. Members of Congress are required to report stock transactions over $1,000 within 45 days. This data is for informational purposes only and does not constitute investment advice.')}
        </p>
      </div>
    </div>
  );
}
